import React, { useState } from 'react'
import { scoreAPI } from '../utils/api'
import Toast from './Toast'

function ScoreForm({ teams, stations, userRole, userStations, onSubmit }) {
  const [step, setStep] = useState(1)
  const [selectedTeam, setSelectedTeam] = useState(null)
  const [scoreType, setScoreType] = useState('')
  const [selectedStation, setSelectedStation] = useState('')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState(null)

  const allowedScoreTypes = {
    staff: ['extra', 'others'],
    staff_station: ['extra', 'station', 'minigame', 'others'],
    admin: ['extra', 'station', 'minigame', 'others'],
  }

  const scoreTypeLabels = {
    extra: '⭐ Extra Score',
    station: '🏪 Station Score',
    minigame: '🎮 Minigame Score',
    others: '📝 Others',
  }

  const handleSubmit = async () => {
    setError('')

    if (!selectedTeam || !scoreType || !amount) {
      setError('Please fill all required fields')
      return
    }

    if (
      (scoreType === 'station' || scoreType === 'minigame') &&
      userRole !== 'admin' &&
      !selectedStation
    ) {
      setError('Please select a station')
      return
    }

    setLoading(true)
    try {
      await scoreAPI.create(
        selectedTeam,
        scoreType,
        parseInt(amount),
        selectedStation || null,
        note
      )

      setToast({ message: '✓ Score added successfully!', type: 'success' })
      resetForm()
      onSubmit?.()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add score')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setStep(1)
    setSelectedTeam(null)
    setScoreType('')
    setSelectedStation('')
    setAmount('')
    setNote('')
  }

  const teamColor = {
    pink: 'bg-pink-500',
    orange: 'bg-orange-500',
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    yellow: 'bg-yellow-500',
    green: 'bg-green-500',
  }

  const teamEmoji = {
    pink: '🩷',
    orange: '🧡',
    blue: '🩵',
    purple: '💜',
    yellow: '💛',
    green: '💚',
  }

  const stationOptions = userRole === 'admin'
    ? stations
    : stations.filter(s => userStations?.includes(s.id))

  return (
    <div>
      {error && (
        <div className="bg-red-50 p-3 rounded text-red-700 mb-4">
          {error}
        </div>
      )}

      {/* Step 1: Select Team */}
      {step === 1 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Step 1: Select Team</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {teams.map((team) => (
              <button
                key={team.id}
                onClick={() => {
                  setSelectedTeam(team.id)
                  setStep(2)
                }}
                className={`p-4 rounded-lg font-semibold text-white transition ${
                  teamColor[team.id]
                } hover:opacity-90`}
              >
                <div className="text-3xl">{teamEmoji[team.id]}</div>
                <div className="text-sm mt-2">{team.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Select Score Type */}
      {step === 2 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Step 2: Select Score Type</h3>
          <div className="space-y-2">
            {allowedScoreTypes[userRole].map((type) => (
              <button
                key={type}
                onClick={() => {
                  setScoreType(type)
                  if (type === 'station' || type === 'minigame') {
                    setStep(3)
                  } else {
                    setStep(4)
                  }
                }}
                className="w-full p-3 border border-gray-300 rounded hover:bg-gray-50 text-left font-semibold"
              >
                {scoreTypeLabels[type]}
              </button>
            ))}
          </div>
          <button
            onClick={() => setStep(1)}
            className="btn btn-secondary mt-4 w-full"
          >
            ← Back
          </button>
        </div>
      )}

      {/* Step 3: Select Station (if needed) */}
      {step === 3 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Step 3: Select Station</h3>
          {stationOptions.length > 0 ? (
            <div className="space-y-2">
              {stationOptions.map((station) => (
                <button
                  key={station.id}
                  onClick={() => {
                    setSelectedStation(station.id)
                    setStep(4)
                  }}
                  className="w-full p-3 border border-gray-300 rounded hover:bg-gray-50 text-left"
                >
                  {station.name}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-gray-600">No stations assigned to you</p>
          )}
          <button
            onClick={() => setStep(2)}
            className="btn btn-secondary mt-4 w-full"
          >
            ← Back
          </button>
        </div>
      )}

      {/* Step 4: Enter Amount */}
      {step === 4 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Step 4: Enter Points</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Points
              </label>
              <input
                type="number"
                min="0"
                max="10000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g., 50"
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note (Optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add any notes or details..."
                className="input h-20"
              />
            </div>

            <div className="bg-gray-50 p-4 rounded space-y-2">
              <p className="text-sm">
                <strong>Team:</strong> {selectedTeam?.toUpperCase()}
              </p>
              <p className="text-sm">
                <strong>Type:</strong> {scoreTypeLabels[scoreType]}
              </p>
              {selectedStation && (
                <p className="text-sm">
                  <strong>Station:</strong> {stationOptions.find(s => s.id === selectedStation)?.name}
                </p>
              )}
              <p className="text-sm">
                <strong>Points:</strong> {amount || 0}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setStep(3)}
                className="btn btn-secondary flex-1"
              >
                ← Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !amount}
                className="btn btn-primary flex-1"
              >
                {loading ? 'Submitting...' : 'Submit Score ✓'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
}

export default ScoreForm
